'use client';

import Image from 'next/image';
import { ClientLogoGridBlock as IClientLogoGridBlock } from '../types';
import { Clients } from '../types';
import { useEffect, useState } from 'react';
import { SupportedLanguages } from '@/types/globals';

const messages = {
  fr: {
    loading: 'Compter tous nos clients satisfaits...',
    noClients: 'Aucun client trouvé.',
  },
  en: {
    loading: 'Counting all of our happy clients...',
    noClients: 'No clients found.',
  },
};

const CLIENTS_PER_REQUEST = 10;

export default function ClientLogoGridBlock({
  locale,
}: {
  block: IClientLogoGridBlock;
  locale: SupportedLanguages;
}) {
  const [allClients, setAllClients] = useState<Clients[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchAllClients() {
      setIsLoading(true);
      try {
        // Get first page and total pages count
        const firstPageResponse = await fetch(
          `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/clients?per_page=${CLIENTS_PER_REQUEST}&page=1&orderby=title&order=asc`,
        );

        if (!firstPageResponse.ok) {
          throw new Error('Failed to fetch clients');
        }

        const totalPages = parseInt(
          firstPageResponse.headers.get('X-WP-TotalPages') || '1',
        );

        // Get first page data
        const firstPageData = await firstPageResponse.json();

        // If we have more pages, fetch them in parallel
        let allData = firstPageData;
        if (totalPages > 1) {
          const remainingPagePromises = Array.from(
            { length: totalPages - 1 },
            (_, i) =>
              fetch(
                `${process.env.NEXT_PUBLIC_WP_JSON}/wp/v2/clients?per_page=${CLIENTS_PER_REQUEST}&page=${i + 2}&orderby=title&order=asc`,
              ).then(res => res.json()),
          );

          const remainingPagesData = await Promise.all(remainingPagePromises);
          allData = [...firstPageData, ...remainingPagesData.flat()];
        }

        // Filter out clients with no title AND no logo
        const validClients = allData.filter(
          (client: Clients) =>
            client.title?.rendered?.trim() || client.acf?.logo?.url,
        );

        setAllClients(validClients);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setAllClients([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAllClients();
  }, []); // Only fetch once on mount

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-qf-purple">{messages[locale].loading}</div>
      </div>
    );
  }

  if (allClients.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="text-qf-purple">{messages[locale].noClients}</div>
      </div>
    );
  }

  return (
    <div className={`mx-auto max-w-7xl p-4 sm:p-6 lg:p-8`}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {allClients.map(client => (
          <div
            key={client.title?.rendered}
            className="rounded-lg bg-white p-3 shadow-sm"
          >
            <div
              className="flex items-center justify-center rounded-lg"
              style={{ width: '100%', height: 80 }}
            >
              {client.acf?.logo?.url ? (
                <Image
                  src={client.acf.logo.url}
                  alt={
                    client.acf.logo.alt ||
                    client.title?.rendered ||
                    'Company logo'
                  }
                  className="h-full w-full rounded-lg object-contain"
                  width={80}
                  height={80}
                />
              ) : (
                <div className="text-qf-purple text-center">
                  <p className="text-xs">
                    No logo for {client.title?.rendered}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
